import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal } from '@nucleus-commerce/admin-kit/forms';
import type { InventoryItem, StockLocation } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { session } from '@/shared/session/session';

import { toAdjustBody } from '../api/mappers';
import { useInventoryItem } from '../hooks/use-inventory-item';
import { useAdjustStock } from '../hooks/use-inventory-mutations';
import { useStockLocations } from '../hooks/use-stock-locations';
import {
  adjustStockSchema,
  type AdjustStockFormInput,
  type AdjustStockFormOutput,
} from '../schemas/adjust-stock-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['quantity', 'reason']);
const FORM_ID = 'change-stock-form';

export interface ChangeStockModalProps {
  storeId: string;
  variant: { id: string; title: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CountForm({
  storeId,
  item,
  locations,
  onClose,
  onPendingChange,
}: {
  storeId: string;
  item: InventoryItem;
  locations: readonly StockLocation[];
  onClose: () => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const { t } = useTranslation('inventory');
  const message = useErrorMessage();
  const adjust = useAdjustStock(storeId);
  const [locationId, setLocationId] = useState(item.levels[0]?.locationId ?? '');
  const level = item.levels.find((candidate) => candidate.locationId === locationId);
  const onHand = level?.onHand ?? 0;
  const reserved = level?.reserved ?? 0;
  const form = useForm<AdjustStockFormInput, unknown, AdjustStockFormOutput>({
    resolver: useI18nResolver(adjustStockSchema(onHand)),
    defaultValues: { kind: 'count', quantity: String(onHand), reason: '' },
  });
  const quantity = useWatch({ control: form.control, name: 'quantity' });
  const typed = /^\d+$/.test(String(quantity).trim()) ? Number(quantity) : null;
  // A count is a fact, so the API allows it below what is reserved; the form only warns.
  const availableAfter = typed === null ? null : typed - reserved;
  const nameOf = (id: string) => locations.find((location) => location.id === id)?.name ?? '—';

  const pickLocation = (id: string) => {
    setLocationId(id);
    const next = item.levels.find((candidate) => candidate.locationId === id);
    form.setValue('quantity', String(next?.onHand ?? 0));
    form.clearErrors('quantity');
  };

  const submit = form.handleSubmit(async (values) => {
    onPendingChange(true);
    try {
      await adjust.mutateAsync(toAdjustBody(values, item.variantId, locationId));
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    } finally {
      onPendingChange(false);
    }
  });

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('columns.location')}</TableHead>
                <TableHead className="text-right">{t('columns.onHand')}</TableHead>
                <TableHead className="text-right">{t('columns.reserved')}</TableHead>
                <TableHead className="text-right">{t('columns.available')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {item.levels.map((row) => (
                <TableRow key={row.locationId}>
                  <TableCell>{nameOf(row.locationId)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.onHand}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.reserved}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.available}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="change-stock-location">{t('changeStock.location')}</Label>
            <Select value={locationId} onValueChange={pickLocation}>
              <SelectTrigger id="change-stock-location" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {item.levels.map((row) => (
                  <SelectItem key={row.locationId} value={row.locationId}>
                    {nameOf(row.locationId)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('changeStock.count')}</FormLabel>
                <FormControl>
                  <Input inputMode="numeric" autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {availableAfter !== null && availableAfter < 0 ? (
          <Alert role="status">
            <AlertDescription>
              {t('adjust.belowReserved', { available: availableAfter })}
            </AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changeStock.reason')}</FormLabel>
              <FormControl>
                <Input placeholder={t('changeStock.reasonPlaceholder')} {...field} />
              </FormControl>
              <FormDescription>{t('changeStock.reasonHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}

function OpenModal({
  storeId,
  variant,
  onClose,
}: {
  storeId: string;
  variant: ChangeStockModalProps['variant'];
  onClose: () => void;
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const { me } = session.useSession();
  const item = useInventoryItem(storeId, variant.id);
  const locations = useStockLocations(storeId);
  const [pending, setPending] = useState(false);
  const store = me?.stores.find((candidate) => candidate.id === storeId)?.name;
  const ready = item.data?.trackInventory === true && item.data.levels.length > 0;

  return (
    <EditModal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('inventory:changeStock.title')}
      description={
        store
          ? t('inventory:changeStock.description', { variant: variant.title, store })
          : variant.title
      }
      footerStart={
        <Link
          to="/s/$storeId/inventory/$variantId"
          params={{ storeId, variantId: variant.id }}
          className="text-primary text-[13px] hover:underline"
        >
          {t('inventory:changeStock.openInInventory')}
        </Link>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={!ready || pending}>
            {pending ? t('common:actions.saving') : t('inventory:changeStock.submit')}
          </Button>
        </>
      }
    >
      {item.isPending || locations.isPending ? (
        <Skeleton className="h-40" />
      ) : item.error ? (
        <ErrorState error={item.error} onRetry={() => void item.refetch()} compact />
      ) : locations.error ? (
        <ErrorState error={locations.error} onRetry={() => void locations.refetch()} compact />
      ) : !ready ? (
        <p className="text-muted-foreground text-sm">{t('inventory:stockCard.notTracked')}</p>
      ) : (
        <CountForm
          storeId={storeId}
          item={item.data}
          locations={locations.data}
          onClose={onClose}
          onPendingChange={setPending}
        />
      )}
    </EditModal>
  );
}

/**
 * A variant's stock in one store: the new on-hand count at a location and the required reason,
 * written to the ledger as a count. Saves at once, off the page's save bar.
 */
export function ChangeStockModal({ storeId, variant, open, onOpenChange }: ChangeStockModalProps) {
  return open ? (
    <OpenModal storeId={storeId} variant={variant} onClose={() => onOpenChange(false)} />
  ) : null;
}
